/**
 * @module router/index
 *
 * Vue Router configuration. Defines all application routes with lazy-loaded
 * components, authentication guards, and admin-only access restrictions.
 *
 * Routes use code-splitting via dynamic imports (import()) so each view
 * component is only loaded when its route is first visited, improving
 * initial page load performance.
 *
 * The global navigation guard (navigationGuard) handles:
 * - Redirecting unauthenticated users to /login
 * - Redirecting non-admin users away from admin pages
 * - Redirecting already-authenticated users away from /login
 */

import { createRouter, createWebHistory } from "vue-router";
import { useStatus } from "@/composables/useStatus";
import { navigationGuard } from "./navigationGuard.js";
// Browser BACK closes an open dialog instead of leaving the page (see the module)
import { installBackButton } from "@/utils/backButton.js";
// NOTE: PlaylistView is lazy-loaded like every other view (see the /playlist
// route below). It must NOT be a static import: it pulls in usePlaylist, which
// calls useMediaPlayer() at module scope, and useMediaPlayer now imports this
// router module — a static PlaylistView import would make that a load-time
// cycle (useMediaPlayer() runs before its own module finishes initializing →
// "Cannot access 'mediaPlayerInstance' before initialization").

// Get the getStatus function and statusMode ref for the status pages'
// beforeEnter guards (both /status and /admin/status share one StatusView.vue
// component — statusMode picks which endpoint it fetches).
const { getStatus, statusMode } = useStatus();

/**
 * Route definitions for the application.
 * Each route specifies:
 * - path: URL path
 * - name: unique route name for programmatic navigation
 * - component: lazy-loaded Vue component
 * - meta.requiresAuth: whether the route requires authentication
 * - meta.requiresAdmin: whether the route requires admin role
 */
const routes = [
  {
    path: "/",
    redirect: "/list-podcasts", // Root redirects to podcasts list
  },
  {
    path: "/add-podcast",
    name: "add-podcast",
    component: () => import("@/components/AddPodcastForm.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/edit-podcast",
    name: "edit-podcast",
    component: () => import("@/components/AddPodcastForm.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/list-podcasts",
    name: "list-podcasts",
    component: () => import("@/components/ListPodcastsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Global "search all episodes" page (replaces the old dialog)
    path: "/search",
    name: "search",
    component: () => import("@/components/SearchView.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Saved searches management page (list, delete, copy RSS feed URL)
    path: "/saved-searches",
    name: "saved-searches",
    component: () => import("@/components/SavedSearchesView.vue"),
    meta: { requiresAuth: true },
  },
  {
    // "Upload an episode file" — sideload a local audio/video file directly
    // into the Miscellanea podcast, no external source involved.
    path: "/sideload",
    name: "sideload",
    component: () => import("@/components/SideloadView.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Dynamic route: podcast ID is a URL parameter
    path: "/list-episodes/:id",
    name: "list-episodes",
    component: () => import("@/components/ListEpisodesView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/list-all-episodes",
    name: "list-all-episodes",
    component: () => import("@/components/ListEpisodesView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/playlist",
    name: "playlist",
    component: () => import("@/components/PlaylistView.vue"),
    meta: { requiresAuth: true },
  },

  {
    // Visore degli articoli archiviati di fides-et-ratio.it (tabella
    // fides_articoli). Griglia di card; cliccarne una legge l'articolo ad
    // alta voce in un episodio, previa conferma.
    path: "/fides",
    name: "fides",
    component: () => import("@/components/FidesView.vue"),
    // Solo amministratori, come le rotte server corrispondenti. Senza questo
    // flag la pagina si aprirebbe comunque e mostrerebbe soltanto errori 403.
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  // The playlist media player is a dedicated full-screen PAGE (not a dialog).
  // "Playlist mode": every playlist-based play action (Play All / Play from
  // here / swipe in Playlist, plus Search & History "Play") funnels through
  // useMediaPlayer.openPlayer(), which router.push()es here — the in-memory
  // playlist singleton is already populated in this same tab, so there's
  // nothing to hand off. (The single-episode "Play Episode" quick-play is a
  // separate DIALOG — EpisodePlayerDialog.vue — and never touches this route.)
  {
    path: "/player",
    name: "player",
    component: () => import("@/components/MediaPlayer.vue"),
    meta: { requiresAuth: true },
    // A hard reload / bookmark straight to /player has no queued playback
    // (the playlist singleton is empty on a cold load). `from.name` is unset
    // ONLY on that first/cold navigation — every in-app openPlayer() nav has a
    // real `from` — so this bounces exactly the reload/deep-link case back to
    // the playlist, and never interferes with a genuine play action.
    beforeEnter: (to, from) => (from.name ? true : { name: "playlist" }),
  },

  {
    path: "/list-latest",
    name: "list-latest",
    component: () => import("@/components/ListLatestView.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/status",
    name: "status",
    component: () => import("@/components/StatusView.vue"),
    /**
     * Before entering the status page, pre-fetch the queue status data
     * so it is available immediately when the component renders. Resets
     * statusMode to 'mine' first — defensive in case the singleton was left
     * on 'all' by a previous visit to /admin/status in this same SPA
     * session (its own beforeEnter below sets 'all'; without resetting it
     * here too, navigating status -> admin-status -> status would leave
     * this personal page silently fetching everyone's jobs).
     */
    beforeEnter: async (to, from, next) => {
      statusMode.value = "mine";
      try {
        await getStatus();
      } catch (error) {
        // Log but don't block navigation — the page will show empty state
        console.error("Failed to pre-fetch status:", error);
      }
      // Always call next() so the page renders even if the API call fails
      next();
    },
    meta: { requiresAuth: true },
  },
  {
    // Admin-only: the SAME status queue data, but for every user, not just
    // the viewer — see StatusView.vue's `route.meta.allUsers` branch and
    // GET /queue/status/all-users. Distinct route/nav entry from /status
    // (which every user, admin or not, has) rather than a toggle on that
    // page, so it's discoverable from the Admin submenu the way every other
    // admin action already is.
    path: "/admin/status",
    name: "admin-status",
    component: () => import("@/components/StatusView.vue"),
    beforeEnter: async (to, from, next) => {
      statusMode.value = "all";
      try {
        await getStatus();
      } catch (error) {
        console.error("Failed to pre-fetch admin status:", error);
      }
      next();
    },
    meta: { requiresAuth: true, requiresAdmin: true, allUsers: true },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/components/LoginForm.vue"),
    // No requiresAuth -- login is accessible without authentication
  },
  {
    path: "/activate",
    name: "activate",
    component: () => import("@/components/ActivateAccount.vue"),
    // No requiresAuth -- this is where an INVITED person creates their account,
    // so by definition they have no credentials yet. The ?token= query param is
    // the only credential, and it's validated server-side.
  },
  {
    path: "/admin",
    name: "admin",
    component: () => import("@/components/AdminView.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin/users",
    name: "admin-users",
    component: () => import("@/components/UsersManagement.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    // Play History — episodes the user has played, most recent first.
    path: "/history",
    name: "history",
    component: () => import("@/components/HistoryView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("@/components/SettingsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Server Settings — the admin-only half of what used to be one mixed
    // /settings page (cache retention, check intervals, feed-download
    // toggle, the cache-cleanup pause, ...). /settings itself stays
    // all-users (Account, Player, Tags, Versions).
    path: "/admin/settings",
    name: "admin-settings",
    component: () => import("@/components/AdminSettingsView.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    // Architecture documentation page: renders ARCHITECTURE.md as formatted HTML.
    // Admin-only — contains internal system details not meant for regular users.
    path: "/admin/architecture",
    name: "admin-architecture",
    component: () => import("@/components/ArchitectureView.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  {
    /**
     * Catch-all: qualunque indirizzo che non corrisponda a nulla.
     *
     * DEVE restare l'ultima rotta dell'array — vue-router sceglie la prima che
     * combacia, e messa prima intercetterebbe tutto il resto.
     *
     * Senza requiresAuth di proposito: "questa pagina non esiste" è una
     * risposta sensata anche a chi non ha fatto login, mentre rimbalzarlo
     * alla pagina di accesso gli farebbe credere che l'indirizzo sia valido
     * ma protetto.
     */
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/components/NotFoundView.vue"),
  },
];

/**
 * Create the Vue Router instance with HTML5 history mode.
 * HTML5 history mode uses real URLs (no hash #) which requires
 * server-side configuration to serve index.html for all routes.
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Apply the global navigation guard that runs before every route change
router.beforeEach(navigationGuard);

// Back/forward with a dialog open closes the dialog and stays on the page.
// Registered AFTER navigationGuard, so auth redirects are decided first.
installBackButton(router);

/**
 * After each navigation, persist the current route name to localStorage
 * so the app can restore the last visited page on next login or reload.
 *
 * Skips the UNAUTHENTICATED routes. "login" is obvious; "activate" matters
 * just as much — it's the last page an invited user visits before their first
 * login, so storing it would make the app redirect them straight back to a
 * now-spent activation link instead of into the app. "not-found" for the same
 * reason: memorizzare un indirizzo sbagliato riporterebbe l'utente sul 404 al
 * prossimo accesso.
 */
const UNAUTHENTICATED_ROUTES = ["login", "activate", "not-found"];

router.afterEach((to) => {
  if (to.name && !UNAUTHENTICATED_ROUTES.includes(to.name)) {
    localStorage.setItem("fyt-last-page", to.name);
  }
});

export default router;
